import { ALPHA2_TO_ALPHA3, COUNTRIES, getCountryName } from "@repo/shared";
import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUserLanguage } from "@/lib/i18n/context";

/**
 * QL-62 — the one country dropdown.
 *
 * The `COUNTRIES` list is hard-ordered by ENGLISH name, but every consumer
 * renders localized labels, so in nb_NO the list read "Tyskland" under G and
 * "Sveits" under S — alphabetical by a name nobody could see. This component
 * sorts by the label it actually renders (locale-aware `Intl.Collator`, so
 * Å/Ø/Æ land where a Norwegian expects them) and pins the Nordic eID markets
 * to the top, since those are the ones advisors pick all day.
 *
 * QL-17 — labels come from the shared country dictionary, the same table the
 * client view and the PDF read, so a country reads the same everywhere.
 */

/** Pinned to the top: the jurisdictions we drive eID for. */
const PRIORITY_CODES = ["NO", "SE", "DK", "FI"] as const;

/** `alpha3` — QL-68, for rules that store the code Sumsub reports. */
type CountryOption = { code: string; alpha3: string; label: string };

export function useCountryOptions(): {
  priority: CountryOption[];
  rest: CountryOption[];
  labelOf: (code: string) => string;
} {
  const { userLanguage } = useUserLanguage();
  const locale = userLanguage.replace("_", "-");

  return useMemo(() => {
    const collator = new Intl.Collator(locale, { sensitivity: "base" });
    const options = COUNTRIES.map((c) => ({
      code: c.code,
      alpha3: c.alpha3,
      label: getCountryName(c.code, userLanguage),
    }));
    const byCode = new Map(options.map((o) => [o.code, o.label]));

    const priority = PRIORITY_CODES.map((code) => ({
      code,
      alpha3: ALPHA2_TO_ALPHA3[code] ?? code,
      label: byCode.get(code) ?? code,
    }));
    const rest = options
      .filter((o) => !(PRIORITY_CODES as readonly string[]).includes(o.code))
      .sort((a, b) => collator.compare(a.label, b.label));

    return { priority, rest, labelOf: (code: string) => byCode.get(code) ?? code };
  }, [locale, userLanguage]);
}

type CountrySelectProps = {
  value: string | null | undefined;
  onValueChange: (code: string) => void;
  placeholder: string;
  /**
   * Renders a Clear button beside the select. A Radix Select cannot return
   * to "nothing selected" on its own, so without this a mis-picked country
   * can never be unset.
   */
  onClear?: () => void;
  clearLabel?: string;
  id?: string;
  className?: string;
};

export function CountrySelect({
  value,
  onValueChange,
  placeholder,
  onClear,
  clearLabel,
  id,
  className,
}: CountrySelectProps) {
  const { priority, rest } = useCountryOptions();

  const renderItem = (option: CountryOption) => (
    <SelectItem key={option.code} value={option.code} textValue={option.label}>
      {option.label} ({option.code})
    </SelectItem>
  );

  const select = (
    // `?? ""`, never `|| undefined`: passing undefined flips Radix from
    // controlled to UNCONTROLLED, so it keeps rendering its own last value
    // and Clear appears to do nothing. Radix shows the placeholder for the
    // empty string just as it does for undefined, so "" is the way to say
    // "nothing selected" without giving up control.
    <Select value={value ?? ""} onValueChange={onValueChange}>
      <SelectTrigger id={id} className={className}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {priority.map(renderItem)}
        <SelectSeparator />
        {rest.map(renderItem)}
      </SelectContent>
    </Select>
  );

  if (!onClear) return select;

  return (
    <div className="flex gap-2">
      {select}
      {value && (
        <Button type="button" variant="outline" size="sm" onClick={onClear}>
          {clearLabel}
        </Button>
      )}
    </div>
  );
}
